"use strict";
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGlobal = exports.addSharedState = exports.curValues = void 0;
var React = require("react");
var ts_functional_1 = require("ts-functional");
// Record all components who are subscribed to specific global states
var subscribers = {};
var getSubscribers = function (index) {
    if (!subscribers[index]) {
        subscribers[index] = new Set();
    }
    return subscribers[index];
};
// Record all spies that are listening to state changes
var spies = {};
var globalSpies = new Set();
var callListeners = function (newVal, oldVal, index) {
    (spies[index] || []).forEach(function (spy) { spy(newVal, oldVal, index); });
    globalSpies.forEach(function (spy) { spy(newVal, oldVal, index); });
};
// Record the last values for all state.  We need this in case the index for a hook changes,
// and we need to manually reset the state
exports.curValues = {};
var setCurValue = function (index, obj) {
    exports.curValues[index] = obj;
};
var updateSubscribers = function (index) { return function (newValOrSetter) {
    // Convert the new value into a setter function if it's a raw value
    var updateVal = typeof newValOrSetter === 'function'
        ? newValOrSetter
        : function () { return newValOrSetter; };
    // We need to wrap the setter function before sending it to the subscribers, so 
    // that the value can updated (if needed) after it's generated.  The wrapper function is
    // memoized, so that the new value is only calculated and updated once.
    var newSetter = ts_functional_1.memoize(function (old) {
        // Update and save the value
        var newVal = updateVal(old);
        if (newVal !== old) {
            setCurValue(index, newVal);
            callListeners(newVal, old, index);
        }
        // Return the new value
        return newVal;
    }, {});
    // Update the subscribers if there are any
    getSubscribers(index).forEach(function (setter) { setter(newSetter); });
}; };
var manageSubscribers = function (index, get, setVal) {
    React.useEffect(function () {
        var subs = getSubscribers(index);
        subs.add(setVal);
        if (exports.curValues[index]) {
            setVal(exports.curValues[index]);
        }
        return function () {
            subs.delete(setVal);
        };
    }, [index, get, setVal]);
};
var useGlobalRaw = function (options) {
    return function (index, initialValue) {
        var get = function () {
            var initial = !!options && !!options.loadInitialValue
                ? options.loadInitialValue(index, initialValue)
                : initialValue;
            if (typeof exports.curValues[index] === "undefined") {
                setCurValue(index, initial);
                callListeners(initial, initial, index);
            }
            return initial;
        };
        var _a = __read(React.useState(index), 2), curIndex = _a[0], setCurIndex = _a[1];
        var _b = __read(React.useState(get), 2), val = _b[0], setVal = _b[1];
        manageSubscribers(index, get, setVal);
        var set = updateSubscribers(index);
        // If the index of this hook changes, we need to manually update the current state based on the last saved value
        React.useEffect(function () {
            if (curIndex !== index) {
                setCurIndex(index);
                set(exports.curValues[index] || get());
            }
        }, [index]);
        return [val, set];
    };
};
useGlobalRaw.listen = {
    on: function (state, spy) {
        var i = state.__index__;
        if (!spies[i]) {
            spies[i] = new Set();
        }
        spies[i].add(spy);
        if (typeof exports.curValues[i] !== "undefined") {
            spy(exports.curValues[i], exports.curValues[i], i);
        }
    },
    onAll: function (spy) {
        globalSpies.add(spy);
        Object.keys(exports.curValues).forEach(function (index) {
            spy(exports.curValues[index], exports.curValues[index], index);
        });
    },
    off: function (state, spy) {
        spies[state.__index__].delete(spy);
    },
    offAll: function (spy) {
        globalSpies.delete(spy);
    },
    clear: function (state) {
        spies[state.__index__].clear();
    },
    clearAll: function () {
        Object.keys(spies).forEach(function (index) {
            spies[index].clear();
        });
        globalSpies.clear();
    }
};
useGlobalRaw.clear = function (index) {
    delete exports.curValues[index];
};
useGlobalRaw.clearAll = function () {
    Object.keys(exports.curValues).forEach(useGlobalRaw.clear);
};
exports.addSharedState = function (index, f) {
    f.__index__ = index;
    f.onChange = function (spy) { exports.useGlobal.listen.on(f, spy); };
    f.offChange = function (spy) { exports.useGlobal.listen.off(f, spy); };
    f.clearListeners = function () { exports.useGlobal.listen.clear(f); };
    f.getValue = function () { return exports.curValues[index]; };
    return f;
};
exports.useGlobal = useGlobalRaw;
